import { statementImportConfirmInputFingerprintV2, statementImportIntentInputFingerprintV2,
  statementImportReconciliationInputFingerprintV2 } from
  "@tradvera/portfolio-domain";
import {
  parseStatementImportConfirmResponseBoundV2, parseStatementImportExtractionResponseV2,
  parseStatementImportIntentResponseBoundV2, parseStatementImportReconciliationResponseV2,
  parseStatementImportReviewResponseV2, parseStatementImportStatusResponseBoundV2,
  parseStatementImportUploadResponseBoundV2,
  STATEMENT_IMPORT_FORMATS_V2,
  type PortfolioStatementImportV2, type StatementImportConfirmInputV2,
  type PortfolioBookKindV2,
  type StatementImportDispositionCountsV2, type StatementImportDiscardInputV2,
  type StatementImportIntentInputV2, type StatementImportIntentV2,
  type StatementImportOperationStatusV2, type StatementImportOperationV2,
  type StatementImportReceiptV2, type StatementImportReconciliationInputV2,
  type StatementImportReviewInputV2, type StatementImportUploadResponseV2,
} from "@tradvera/contracts";
import type { MyPortfolioNextAccount } from "../client.js";
import { createStatementRequestV2, exactStatementObjectKeysV2, invalidStatementResponseV2,
  statementResponseErrorV2, StatementImportClientErrorV2 } from "./client-transport";
import { statementImportActionUrlV2, statementImportAdminUnlockUrlV2,
  statementImportCapabilitiesUrlV2, statementImportCollectionUrlV2,
  statementImportDetailUrlV2, statementImportEvidenceUrlV2,
  statementImportOperationStatusUrlV2 } from "./routes";
import type { PreparedBrowserStatementUploadV2 } from "./upload";

export { StatementImportClientErrorV2 } from "./client-transport";

export interface StatementImportEvidenceRequestV2 {
  importId: string; evidenceToken: string; mediaType: string; bytes: number; fileSha256: string;
}

export interface StatementExtractionCapabilityV2 {
  id: string; available: boolean; version?: string; reason?: string;
}
export interface StatementImportCapabilitiesV2 {
  formats: readonly ["CSV", "OFX", "QFX", "PDF", "IMAGE"];
  maxUploadBytes: number;
  capabilities: readonly StatementExtractionCapabilityV2[];
}

export interface StatementImportClientV2 {
  capabilities(signal?: AbortSignal): Promise<StatementImportCapabilitiesV2>;
  list(signal?: AbortSignal): Promise<PortfolioStatementImportV2[]>;
  detail(importId: string, signal?: AbortSignal): Promise<PortfolioStatementImportV2>;
  upload(input: PreparedBrowserStatementUploadV2, signal?: AbortSignal): Promise<StatementImportUploadResponseV2>;
  review(input: StatementImportReviewInputV2, signal?: AbortSignal): Promise<PortfolioStatementImportV2>;
  reconcile(input: StatementImportReconciliationInputV2, signal?: AbortSignal): Promise<PortfolioStatementImportV2>;
  createIntent(input: StatementImportIntentInputV2, dispositionCounts: StatementImportDispositionCountsV2,
    signal?: AbortSignal): Promise<StatementImportIntentV2>;
  confirm(input: StatementImportConfirmInputV2, dispositionCounts: StatementImportDispositionCountsV2,
    signal?: AbortSignal): Promise<StatementImportReceiptV2>;
  discard(input: StatementImportDiscardInputV2, signal?: AbortSignal): Promise<PortfolioStatementImportV2>;
  evidence(input: StatementImportEvidenceRequestV2, signal?: AbortSignal): Promise<Blob>;
  operationStatus(input: { operation: StatementImportOperationV2; importId: string | null;
    idempotencyKey: string; inputFingerprint: string;
    dispositionCounts: StatementImportDispositionCountsV2 | null },
    signal?: AbortSignal): Promise<StatementImportOperationStatusV2>;
}

export function createStatementImportClientV2(options: {
  account: Pick<MyPortfolioNextAccount, "getIdToken">; adminSession?: string;
  booksEndpoint: string; bookId: string; bookKind?: PortfolioBookKindV2; fetchImpl?: typeof fetch;
}): StatementImportClientV2 {
  if (options.adminSession && (options.adminSession.length < 32 || options.adminSession.length > 100)) {
    throw new StatementImportClientErrorV2("ADMIN_SESSION_REQUIRED", 401);
  }
  const request = createStatementRequestV2(options.account, options.adminSession, options.fetchImpl ?? fetch);
  const bookKind = options.bookKind ?? "OWNED_PRIVATE";
  const collection = statementImportCollectionUrlV2(options.booksEndpoint, options.bookId, bookKind);
  const readImport = (payload: unknown, importId: string,
    parse: (value: unknown) => { import: PortfolioStatementImportV2 }) => {
    const imported = parseRead(() => parse(payload).import);
    assertImportBinding(imported, options.bookId, importId, bookKind); return imported;
  };
  return {
    async capabilities(signal) {
      return parseCapabilities(await request.json(statementImportCapabilitiesUrlV2(options.booksEndpoint,
        options.bookId, bookKind), { method: "GET", cache: "no-store", signal }));
    },
    async list(signal) {
      return parseStatementImportListV2(await request.json(collection,
        { method: "GET", cache: "no-store", signal }), options.bookId, bookKind);
    },
    async detail(importId, signal) {
      const payload = await request.json(statementImportDetailUrlV2(options.booksEndpoint,
        options.bookId, importId, bookKind), { method: "GET", cache: "no-store", signal });
      return readImport(payload, importId, parseStatementImportExtractionResponseV2);
    },
    async upload(input, signal) {
      const body = new FormData(); body.append("statement", input.file, input.file.name);
      const payload = await request.json(collection, { method: "POST", cache: "no-store", body, signal,
        headers: { "x-tradvera-idempotency-key": input.idempotencyKey,
          "x-tradvera-statement-input-fingerprint": input.inputFingerprint } });
      const parsed = parseRead(() => parseStatementImportUploadResponseBoundV2(payload, {
        bookId: options.bookId, idempotencyKey: input.idempotencyKey,
        inputFingerprint: input.inputFingerprint, fileSha256: input.fileSha256,
        mediaType: input.mediaType, bytes: input.bytes, multipartFileCount: 1, multipartTextFieldCount: 0,
      }));
      const expectedFormat = ["JPG", "PNG", "WEBP", "TIFF"].includes(input.choice) ? "IMAGE" : input.choice;
      if (parsed.import.file.format !== expectedFormat || parsed.import.file.originalName !== input.file.name) invalidResponse();
      return parsed;
    },
    async review(input, signal) {
      const payload = await postJson(request.json, statementImportActionUrlV2(options.booksEndpoint,
        options.bookId, input.importId, "review", bookKind), input, signal);
      const imported = readImport(payload, input.importId, parseStatementImportReviewResponseV2);
      if (imported.reviewRevision !== input.expectedRevision + 1) invalidResponse();
      return imported;
    },
    async reconcile(input, signal) {
      const payload = await postJson(request.json, statementImportActionUrlV2(options.booksEndpoint,
        options.bookId, input.importId, "reconciliation", bookKind), input, signal);
      const imported = readImport(payload, input.importId, parseStatementImportReconciliationResponseV2);
      if (imported.reviewRevision !== input.expectedRevision
        || imported.reconciliation.inputFingerprint !== statementImportReconciliationInputFingerprintV2(input)) invalidResponse();
      return imported;
    },
    async createIntent(input, dispositionCounts, signal) {
      const payload = await postJson(request.json, statementImportActionUrlV2(options.booksEndpoint,
        options.bookId, input.importId, "intents", bookKind), input, signal);
      return parseRead(() => parseStatementImportIntentResponseBoundV2(payload, { importId: input.importId,
        bookId: options.bookId, inputFingerprint: statementImportIntentInputFingerprintV2(input),
        dispositionCounts }).intent);
    },
    async confirm(input, dispositionCounts, signal) {
      const payload = await postJson(request.json, statementImportActionUrlV2(options.booksEndpoint,
        options.bookId, input.importId, "confirmations", bookKind), input, signal);
      return parseRead(() => parseStatementImportConfirmResponseBoundV2(payload, { importId: input.importId,
        bookId: options.bookId, inputFingerprint: statementImportConfirmInputFingerprintV2(input),
        dispositionCounts }).receipt);
    },
    async discard(input, signal) {
      const payload = await postJson(request.json, statementImportActionUrlV2(options.booksEndpoint,
        options.bookId, input.importId, "discard-confirmations", bookKind), input, signal);
      const imported = readImport(payload, input.importId, parseStatementImportReviewResponseV2);
      if (imported.state !== "ABANDONED") invalidResponse(); return imported;
    },
    async evidence(input, signal) {
      const response = await request.response(statementImportEvidenceUrlV2(options.booksEndpoint,
        options.bookId, input.importId, input.evidenceToken, bookKind), { method: "GET", cache: "no-store", signal });
      const mediaType = response.headers.get("content-type")?.split(";", 1)[0]?.trim() ?? "";
      const declared = response.headers.get("content-length");
      if (mediaType !== input.mediaType || (declared !== null && Number(declared) !== input.bytes)) invalidResponse();
      const bytes = await response.arrayBuffer();
      if (bytes.byteLength !== input.bytes || await sha256HexV2(bytes) !== input.fileSha256) invalidResponse();
      return new Blob([bytes], { type: mediaType });
    },
    async operationStatus(input, signal) {
      const url = statementImportOperationStatusUrlV2(options.booksEndpoint, options.bookId,
        input.operation, input.idempotencyKey, input.inputFingerprint, bookKind);
      const payload = await request.json(url, { method: "GET", cache: "no-store", signal });
      const status = parseRead(() => parseStatementImportStatusResponseBoundV2(payload, {
        operation: input.operation, importId: null, bookId: options.bookId,
        idempotencyKey: input.idempotencyKey, inputFingerprint: input.inputFingerprint,
        dispositionCounts: input.dispositionCounts,
      }).status);
      if (input.importId !== null && status.importId !== null && status.importId !== input.importId) invalidResponse();
      return status;
    },
  };
}

export async function unlockStatementImportAdminSessionV2(options: {
  account: Pick<MyPortfolioNextAccount, "getIdToken">; booksEndpoint: string; pin: string; fetchImpl?: typeof fetch;
}): Promise<{ adminSession: string; expiresAt: string; forcePinChange: boolean }> {
  if (!/^\d{4}$/u.test(options.pin)) throw new StatementImportClientErrorV2("PIN_FORMAT_INVALID", 422);
  const token = await options.account.getIdToken();
  const response = await (options.fetchImpl ?? fetch)(statementImportAdminUnlockUrlV2(options.booksEndpoint), {
    method: "POST", cache: "no-store", headers: { authorization: `Bearer ${token}`,
      "content-type": "application/json" }, body: JSON.stringify({ pin: options.pin }) });
  if (!response.ok) throw await statementResponseErrorV2(response);
  let value: unknown; try { value = await response.json(); } catch { invalidResponse(); }
  if (!exactStatementObjectKeysV2(value, ["adminSession", "expiresAt", "forcePinChange", "admin"])
    || typeof value.adminSession !== "string" || value.adminSession.length < 32 || value.adminSession.length > 100
    || typeof value.expiresAt !== "string" || !Number.isFinite(Date.parse(value.expiresAt))
    || typeof value.forcePinChange !== "boolean") invalidResponse();
  return { adminSession: value.adminSession, expiresAt: new Date(value.expiresAt).toISOString(),
    forcePinChange: value.forcePinChange };
}

export function newStatementOperationIdempotencyKeyV2(operation: string): string {
  if (!globalThis.crypto?.randomUUID) throw new Error("STATEMENT_IMPORT_SECURE_OPERATION_ID_UNAVAILABLE");
  return `statement-${operation.toLowerCase()}:${globalThis.crypto.randomUUID()}`;
}

export function statementImportWriteOutcomeUnknownV2(error: unknown): boolean {
  if (error instanceof StatementImportClientErrorV2 && error.code === "STATEMENT_IMPORT_RESPONSE_INVALID") return true;
  const status = error instanceof StatementImportClientErrorV2 ? error.status : undefined;
  if (status === 409) return !(error instanceof StatementImportClientErrorV2)
    || ["STATEMENT_IMPORT_IDEMPOTENCY_CONFLICT", "STATEMENT_IMPORT_OUTCOME_UNKNOWN", "STATEMENT_IMPORT_HTTP_409"]
      .includes(error.code);
  if (status === 408 || status === 429 || (status !== undefined && status >= 500)) return true;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return error instanceof TypeError || /Failed to fetch|NETWORK|ABORT/iu.test(error instanceof Error ? error.message : "");
}

async function postJson(request: (url: string, init: RequestInit) => Promise<unknown>,
  url: string, input: unknown, signal?: AbortSignal): Promise<unknown> {
  return request(url, { method: "POST", cache: "no-store", signal,
    headers: { "content-type": "application/json" }, body: JSON.stringify(input) });
}

function parseStatementImportListV2(value: unknown, bookId: string,
  bookKind: PortfolioBookKindV2): PortfolioStatementImportV2[] {
  if (!exactStatementObjectKeysV2(value, ["ok", "imports"]) || value.ok !== true || !Array.isArray(value.imports)) invalidResponse();
  const imports = value.imports.map((item) => parseRead(() =>
    parseStatementImportReviewResponseV2({ ok: true, import: item }).import));
  if (new Set(imports.map((item) => item.importId)).size !== imports.length
    || imports.some((item) => item.bookId !== bookId || item.bookKind !== bookKind)) invalidResponse();
  return imports;
}

function parseCapabilities(value: unknown): StatementImportCapabilitiesV2 {
  if (!exactStatementObjectKeysV2(value, ["ok", "formats", "maxUploadBytes", "capabilities"])
    || value.ok !== true || !Array.isArray(value.formats)
    || value.formats.length !== STATEMENT_IMPORT_FORMATS_V2.length
    || value.formats.some((item, index) => item !== STATEMENT_IMPORT_FORMATS_V2[index])
    || !Number.isSafeInteger(value.maxUploadBytes) || Number(value.maxUploadBytes) <= 0
    || !Array.isArray(value.capabilities)) invalidResponse();
  const capabilities = value.capabilities.map((item) => parseCapability(item));
  if (new Set(capabilities.map((item) => item.id)).size !== capabilities.length
    || ["parser.csv", "parser.ofx-qfx", "parser.pdf-native", "parser.pdf-scanned", "parser.image-ocr"]
      .some((id) => !capabilities.some((item) => item.id === id))) invalidResponse();
  return { formats: STATEMENT_IMPORT_FORMATS_V2, maxUploadBytes: Number(value.maxUploadBytes), capabilities };
}

function parseCapability(value: unknown): StatementExtractionCapabilityV2 {
  if (!value || typeof value !== "object" || Array.isArray(value)) invalidResponse();
  const source = value as Record<string, unknown>;
  const allowed = new Set(["id", "available", "version", "reason"]);
  if (Object.keys(source).some((key) => !allowed.has(key)) || !Object.hasOwn(source, "id")
    || !Object.hasOwn(source, "available") || typeof source.id !== "string"
    || !/^[a-z0-9.+-]{3,80}$/u.test(source.id) || typeof source.available !== "boolean"
    || (source.version !== undefined && (typeof source.version !== "string" || source.version.length > 200))
    || (source.reason !== undefined && (typeof source.reason !== "string" || source.reason.length > 500))
    || (source.available === false && typeof source.reason !== "string")) invalidResponse();
  return { id: source.id, available: source.available,
    ...(typeof source.version === "string" ? { version: source.version } : {}),
    ...(typeof source.reason === "string" ? { reason: source.reason } : {}) };
}

function assertImportBinding(value: PortfolioStatementImportV2, bookId: string, importId: string,
  bookKind: PortfolioBookKindV2): void {
  if (value.bookId !== bookId || value.importId !== importId || value.bookKind !== bookKind) invalidResponse();
}
function parseRead<T>(callback: () => T): T { try { return callback(); } catch { return invalidResponse(); } }
function invalidResponse(): never { return invalidStatementResponseV2(); }
async function sha256HexV2(bytes: ArrayBuffer): Promise<string> {
  if (!globalThis.crypto?.subtle) invalidResponse();
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}
